import { Link } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import { Mail, Phone, Clock, MapPin } from 'lucide-react';

export default function MetaFooter() {
  const { openPopup } = usePopup();

  const trademarkLinks = [
    { label: "Register Your Trademark", to: "/services/register-your-trademark" },
    { label: "Clearance Search", to: "/services/clearance-search-services" },
    { label: "Trademark Monitoring", to: "/services/trademark-monitoring" },
    { label: "Office Action Response", to: "/services/trademark-office-actions" },
    { label: "File TTAB Petition", to: "/services/file-ttab-petition" },
    { label: "Ownership Transfers", to: "/services/trademark-ownership-transfers" },
  ];

  const businessLinks = [
    { label: "LLC Formation", to: "/services/limited-liability-company" },
    { label: "C Corporation", to: "/services/c-corporation" },
    { label: "S Corporation", to: "/services/s-corporation" },
    { label: "Non-Profit", to: "/services/non-profit" },
    { label: "Copyright Registration", to: "/services/register-your-copyright" },
    { label: "Patent Search", to: "/services/search-for-existing-patent" },
  ];

  const moreLinks = [
    { label: "Amazon Brand Registry", to: "/services/amazon-brand-registry" },
    { label: "Bookkeeping Service", to: "/services/bookkeeping-service" },
    { label: "Tax Advisory & Filing", to: "/services/tax-advisory-filing" },
    { label: "CFO Services", to: "/services/cfo-services" },
    { label: "Business Immigration", to: "/services/business-immigration-law" },
    { label: "Data Protection Law", to: "/services/data-protection-privacy-law" },
  ];

  const companyLinks = [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about" },
    { label: "Contact Us", to: "/contact" },
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms of Service", to: "/terms-of-service" },
  ];

  const linkClass = "text-[#A0AABF]/70 hover:text-[#D4AF37] transition-colors text-[13px] leading-relaxed block py-[3px]";
  const headingClass = "text-white font-semibold text-sm font-['Outfit'] tracking-wide mb-5 uppercase text-[12px] tracking-[0.1em]";

  return (
    <footer data-testid="meta-footer" className="bg-[#060C1C]">
      {/* Top gold accent */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

      {/* Main footer content */}
      <div className="container mx-auto px-6 md:px-12 pt-14 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6">

          {/* Brand + Contact — spans 4 cols */}
          <div className="lg:col-span-4 lg:pr-8">
           <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/assets/img/brand/logo-light.png" alt="Trademark Legal Forum" className="h-8 sm:h-12 w-auto mb-4" />
          
        </Link>
            <p className="text-[#A0AABF]/60 text-[13px] leading-relaxed mb-7">
              Expert trademark registration and intellectual property services across all 50 US states. We help businesses protect their most valuable asset — their brand.
            </p>

            {/* Contact Info */}
            <div className="space-y-1.5 mb-7">
              <div className="flex items-center gap-2.5">
                <Mail size={13} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-[#A0AABF]/80 text-[13px]">info@trademarklegalforum.com</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone size={13} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-[#A0AABF]/80 text-[13px]">+1 (877) 787-8180	</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Clock size={13} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-[#A0AABF]/80 text-[13px]">Mon – Fri: 9:00 AM – 6:00 PM EST</span>
              </div>
              <div className="flex items-center gap-2.5">
                <MapPin size={13} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-[#A0AABF]/80 text-[13px]">1370 N St Andrews Pl, Los Angeles, CA 90028</span>
              </div>
            </div>

            <button
              data-testid="footer-register-btn"
              onClick={() => openPopup()}
              className="bg-[#D4AF37] text-[#0A1128] font-bold py-2.5 px-7 rounded-sm text-xs transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.25)] font-['Outfit'] tracking-wider uppercase"
            >
              Get Started
            </button>
          </div>

          {/* Link columns — span 8 cols, divided into 4 equal parts */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              {/* Trademark Services */}
              <div>
                <h4 className={headingClass}>Trademark</h4>
                <nav>
                  {trademarkLinks.map(link => (
                    <Link key={link.to} to={link.to} className={linkClass}>{link.label}</Link>
                  ))}
                </nav>
              </div>

              {/* Business Services */}
              <div>
                <h4 className={headingClass}>Business</h4>
                <nav>
                  {businessLinks.map(link => (
                    <Link key={link.to} to={link.to} className={linkClass}>{link.label}</Link>
                  ))}
                </nav>
              </div>

              {/* More Services */}
              <div>
                <h4 className={headingClass}>More Services</h4>
                <nav>
                  {moreLinks.map(link => (
                    <Link key={link.to} to={link.to} className={linkClass}>{link.label}</Link>
                  ))}
                </nav>
              </div>

              {/* Company */}
              <div>
                <h4 className={headingClass}>Company</h4>
                <nav>
                  {companyLinks.map(link => (
                    <Link key={link.to} to={link.to} className={linkClass}>{link.label}</Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#1D2D50]/20">
        <div className="container mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#A0AABF]/40 text-xs font-['Manrope']">
            &copy; 2026 Trademark Legal Forum. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/privacy-policy" data-testid="meta-footer-privacy" className="text-[#A0AABF]/40 hover:text-[#D4AF37] transition-colors font-['Manrope']">
              Privacy Policy
            </Link>
            <span className="text-[#1D2D50]/30">|</span>
            <Link to="/terms-of-service" data-testid="meta-footer-terms" className="text-[#A0AABF]/40 hover:text-[#D4AF37] transition-colors font-['Manrope']">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
